import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, vModelText as _vModelText, withDirectives as _withDirectives, vModelSelect as _vModelSelect, vModelCheckbox as _vModelCheckbox, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "container",
  style: {"margin-top":"80px","padding-top":"40px"}
}
const _hoisted_2 = { class: "row" }
const _hoisted_3 = { class: "col-lg-6" }
const _hoisted_4 = { style: {"font-size":"2em","font-weight":"300","color":"#33475b","margin":"0"} }
const _hoisted_5 = { class: "col-lg-6 text-right align-self-end" }
const _hoisted_6 = ["disabled"]
const _hoisted_7 = {
  key: 0,
  class: "alert alert-danger mb-4"
}
const _hoisted_8 = { class: "form-group mb-4" }
const _hoisted_9 = { class: "error" }
const _hoisted_10 = { class: "form-group mb-4" }
const _hoisted_11 = { class: "form-row" }
const _hoisted_12 = { class: "form-group mb-4 col" }
const _hoisted_13 = { class: "form-row" }
const _hoisted_14 = { class: "form-group mb-4 col" }
const _hoisted_15 = {
  key: 0,
  class: "text-danger mb-2"
}
const _hoisted_16 = { class: "form-check mb-3" }
const _hoisted_17 = { key: 1 }
const _hoisted_18 = { class: "font-weight-bold text-secondary mb-2" }
const _hoisted_19 = { class: "pl-2" }
const _hoisted_20 = ["id", "onUpdate:modelValue"]
const _hoisted_21 = ["for"]

import { computed, onMounted, ref, watch, type Ref } from 'vue'
    import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router'
    import { EditorState } from '@common/Common'
    import CorporateNavigation from '@common/Components/CorporateNavigation.vue'
    import { guid } from '@common/Guid'
    import { CurrencyDenomination, MonthYear, MonthYearHelpers, ValidationError } from '@common/types'
    import { useOpexMetricConfigurationQueryV1 } from '@Quest/Metrics/queries-vue-composables'
    import { useCreateOpexMetricCommandV1, useUpdateOpexMetricCommandV1 } from '@Quest/Metrics/commands-vue-composables'
    import type { OpexMetricConfigurationReadModel, OpexAccountMetricItemReadModel } from '@Quest/Metrics/readmodels'
    import { ValueBalanceType, ValueCommitmentType } from '@Quest/Metrics/types'
    import MetricTimeWindowSettings from '@Quest/Metrics/Components/MetricTimeWindowSettings.vue'
    import MetricScheduleDependentSettings from '@Quest/Metrics/Components/MetricScheduleDependentSettings.vue'

    
export default /*@__PURE__*/_defineComponent({
  __name: 'OpexMetricConfigurationPage',
  setup(__props) {

    const route = useRoute()
    const router = useRouter()

    const workspaceId = computed(() => Number(route.params?.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params?.strategyId ?? ''))
    const pageMode = computed(() => route.meta.mode as EditorState)
    const isEditMode = computed(() => pageMode.value === EditorState.Editor)


    const {
        runOpexMetricConfigurationQueryV1,
        modelOpexMetricConfigurationQueryV1
    } = useOpexMetricConfigurationQueryV1(workspaceId, strategyId)

    const rm = modelOpexMetricConfigurationQueryV1 as Ref<OpexMetricConfigurationReadModel | null>

    const {
        invokeCreateOpexMetricCommandV1,
        validationErrorsCreateOpexMetricCommandV1,
        onSucceedCreateOpexMetricCommandV1
    } = useCreateOpexMetricCommandV1(workspaceId, strategyId)

    const {
        invokeUpdateOpexMetricCommandV1,
        validationErrorsUpdateOpexMetricCommandV1,
        onSucceedUpdateOpexMetricCommandV1
    } = useUpdateOpexMetricCommandV1(workspaceId, strategyId)

    const initialized = ref(false)
    const errors = ref<ValidationError[]>([])
    const isSaving = ref(false)
    const allowRouteLeave = ref(false)

    const name = ref<string | null>(null)
    const description = ref<string | null>(null)

    const startDateBoundary = ref<MonthYear | null>(null)
    const endDateBoundary = ref<MonthYear | null>(null)
    const defaultProjectStartDate = ref<MonthYear | null>(null)

    const valueBalanceType = ref<ValueBalanceType>(ValueBalanceType.Costs)
    const denomination = ref<CurrencyDenomination>(CurrencyDenomination.Unit)

    const windowStartOffsetInMonths = ref<number | null>(null)
    const windowLengthInMonths = ref<number | null>(null)

    const accounts = computed(() => rm.value?.Accounts ?? [])
    const costIds = computed(() => accounts.value.filter(a => a.IsIncluded).map(a => a.Id) ?? [])

    const applyBenefitsConfidenceFactor = ref(false)
    const valueCommitmentType = ref<ValueCommitmentType>(ValueCommitmentType.Total)

    const pageHeading = computed(() => isEditMode.value ? "Edit the metric" : "New cost subtotal metric")
    const saveButtonText = computed(() => isSaving.value ? "Saving..." : "Save changes")
    const nameError = computed(() => errors.value.find(e => e.ErrorCode.startsWith("Name."))?.Error ?? null)
    const costIdsError = computed(() => errors.value.find(e => e.ErrorCode.startsWith("CostIds."))?.Error ?? null)

    const totalOpex = ref<boolean>(true)

    const hasChanges = computed(() =>
    {
        if (!isEditMode.value) return true

        const model = rm.value
        if (!model) return true

        const s = model.ScheduleDependentSettings

        return (
            name.value !== model.Name ||
            description.value !== model.Description ||
            valueBalanceType.value !== model.ValueBalanceType ||
            denomination.value !== model.Denomination ||
            windowStartOffsetInMonths.value !== model.WindowStartOffsetInMonths ||
            windowLengthInMonths.value !== model.WindowLengthInMonths ||
            applyBenefitsConfidenceFactor.value !== s?.ApplyBenefitsConfidenceFactor ||
            valueCommitmentType.value !== s?.ValueCommitmentType ||
            !MonthYearHelpers.areEqual(defaultProjectStartDate.value, s?.DefaultProjectStartDate) ||
            !MonthYearHelpers.areEqual(startDateBoundary.value, s?.StartDateBoundary) ||
            !MonthYearHelpers.areEqual(endDateBoundary.value, s?.EndDateBoundary) ||
            !areCostIdsEqualIgnoringOrder(costIds.value, model.CostIds)
        )
    })

    const groupedAccounts = computed(() =>
    {
        const groups: Record<string, OpexAccountMetricItemReadModel[]> = {}

        for (const acc of accounts.value)
        {
            if (!groups[acc.Type])
                groups[acc.Type] = []

            groups[acc.Type].push(acc)
        }

        return Object.entries(groups).map(([type, items]) => ({
            type,
            items
        }))
    })

    function handleCancel()
    {
        if (isEditMode.value)
        {
            router.go(0)
        } else
        {
            router.go(-1)
        }
    }

    function handleBack()
    {
        router.go(-1)
    }

    function handleSave()
    {
        isSaving.value = true
        errors.value = []

        const ids = totalOpex.value === true ? null : costIds.value

        const payload = {
            id: isEditMode.value ? rm.value?.Id : guid(),
            name: name.value,
            description: description.value,
            valueBalanceType: valueBalanceType.value,
            denomination: denomination.value,
            windowStartOffsetInMonths: windowStartOffsetInMonths.value,
            windowLengthInMonths: windowLengthInMonths.value,
            defaultProjectStartDate: defaultProjectStartDate.value,
            startDateBoundary: startDateBoundary.value,
            endDateBoundary: endDateBoundary.value,
            valueCommitmentType: valueCommitmentType.value,
            applyBenefitsConfidenceFactor: applyBenefitsConfidenceFactor.value,
            costIds: ids
        }

        if (isEditMode.value)
        {
            invokeUpdateOpexMetricCommandV1(payload)
        }
        else
        {
            invokeCreateOpexMetricCommandV1(payload)
        }
    }

    function areCostIdsEqualIgnoringOrder(a: string[], b: string[]): boolean
    {
        if (a === b) return true

        const setA = new Set(a)
        const setB = new Set(b)

        if (setA.size !== setB.size) return false

        for (const v of setA)
        {
            if (!setB.has(v)) return false
        }

        return true
    }


    onMounted(() =>
    {
        document.title = "Opex Metric Configuration"

        runOpexMetricConfigurationQueryV1({
            MetricId: route.params.metricId ?? null
        })
    })

    watch(rm, (model) =>
    {
        if (!model || initialized.value) return

        name.value = model.Name
        description.value = model.Description

        valueBalanceType.value = model.ValueBalanceType
        denomination.value = model.Denomination
        windowStartOffsetInMonths.value = model.WindowStartOffsetInMonths
        windowLengthInMonths.value = model.WindowLengthInMonths

        applyBenefitsConfidenceFactor.value = model.ScheduleDependentSettings?.ApplyBenefitsConfidenceFactor ?? false
        valueCommitmentType.value = model.ScheduleDependentSettings?.ValueCommitmentType ?? ValueCommitmentType.Total

        defaultProjectStartDate.value = MonthYearHelpers.copyNullable(model.ScheduleDependentSettings?.DefaultProjectStartDate)
        startDateBoundary.value = MonthYearHelpers.copyNullable(model.ScheduleDependentSettings?.StartDateBoundary)
        endDateBoundary.value = MonthYearHelpers.copyNullable(model.ScheduleDependentSettings?.EndDateBoundary)

        totalOpex.value = model.CostIds.length === 0;

        initialized.value = true
    })


    onSucceedCreateOpexMetricCommandV1(() =>
    {
        allowRouteLeave.value = true
        router.go(-1)
    })

    onSucceedUpdateOpexMetricCommandV1(() =>
    {
        allowRouteLeave.value = true
        router.go(0)
    })

    watch(totalOpex, (value) =>
    {
        if (value)
        {
            accounts.value.forEach(a => a.IsIncluded = false)
        }
    })


    watch(validationErrorsCreateOpexMetricCommandV1, (validationErrors) =>
    {
        if (!validationErrors || validationErrors.length === 0) return
        errors.value = validationErrors
        isSaving.value = false
    })

    watch(validationErrorsUpdateOpexMetricCommandV1, (validationErrors) =>
    {
        if (!validationErrors || validationErrors.length === 0) return
        errors.value = validationErrors
        isSaving.value = false
    })

    onBeforeRouteLeave((to, from, next) =>
    {
        if (allowRouteLeave.value)
        {
            next()
            return
        }

        if (hasChanges.value && !isSaving.value)
        {
            if (confirm('You have unsaved changes. Do you want to save them before leaving?'))
            {
                handleSave()
            }
            else
            {
                next()
            }
        } else
        {
            next()
        }
    })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(CorporateNavigation, { ActivePage: "metrics" }),
    (initialized.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("h6", null, [
                _createElementVNode("a", {
                  class: "text-dark",
                  onClick: handleBack,
                  "data-testid": "cost-subtotal-metric-config-back"
                }, [...(_cache[12] || (_cache[12] = [
                  _createElementVNode("i", {
                    class: "fa fa-angle-double-left",
                    "aria-hidden": "true"
                  }, null, -1 /* CACHED */),
                  _createTextVNode(" Back ", -1 /* CACHED */)
                ]))])
              ]),
              _createElementVNode("h1", _hoisted_4, _toDisplayString(pageHeading.value), 1 /* TEXT */)
            ]),
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("button", {
                type: "submit",
                form: "metric-configuration-form",
                class: _normalizeClass(["mr-1", hasChanges.value ? 'btn btn-warning' : 'btn btn-outline-secondary']),
                disabled: isSaving.value,
                "data-testid": "cost-subtotal-metric-config-save",
                style: {"padding":"0.7em 2em"}
              }, _toDisplayString(saveButtonText.value), 11 /* TEXT, CLASS, PROPS */, _hoisted_6),
              (hasChanges.value)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    type: "button",
                    class: "btn btn-outline-secondary",
                    style: {"padding":"0.7em 2em"},
                    onClick: handleCancel,
                    "data-testid": "cost-subtotal-metric-config-cancel"
                  }, " Cancel "))
                : _createCommentVNode("v-if", true)
            ])
          ]),
          _cache[21] || (_cache[21] = _createElementVNode("hr", { class: "mb-5" }, null, -1 /* CACHED */)),
          _createElementVNode("form", {
            id: "metric-configuration-form",
            onSubmit: _withModifiers(handleSave, ["prevent"]),
            class: "mb-4",
            "data-testid": "cost-subtotal-metric-config-form"
          }, [
            (errors.value.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(errors.value, (e) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: e.ErrorCode
                    }, _toDisplayString(e.Error), 1 /* TEXT */))
                  }), 128 /* KEYED_FRAGMENT */))
                ]))
              : _createCommentVNode("v-if", true),
            _createElementVNode("div", _hoisted_8, [
              _cache[13] || (_cache[13] = _createElementVNode("label", null, "Metric Name:", -1 /* CACHED */)),
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((name).value = $event)),
                type: "text",
                class: "form-control",
                required: "",
                maxlength: "100",
                minlength: "2",
                "data-testid": "cost-subtotal-metric-config-name"
              }, null, 512 /* NEED_PATCH */), [
                [
                  _vModelText,
                  name.value,
                  void 0,
                  { trim: true }
                ]
              ]),
              _createElementVNode("div", _hoisted_9, _toDisplayString(nameError.value), 1 /* TEXT */)
            ]),
            _createElementVNode("div", _hoisted_10, [
              _cache[14] || (_cache[14] = _createElementVNode("label", null, "Metric Description:", -1 /* CACHED */)),
              _withDirectives(_createElementVNode("textarea", {
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((description).value = $event)),
                class: "form-control",
                "data-testid": "cost-subtotal-metric-config-description"
              }, null, 512 /* NEED_PATCH */), [
                [
                  _vModelText,
                  description.value,
                  void 0,
                  { trim: true }
                ]
              ])
            ]),
            _createElementVNode("div", _hoisted_11, [
              _createElementVNode("div", _hoisted_12, [
                _cache[16] || (_cache[16] = _createElementVNode("label", {
                  for: "valueBalanceType",
                  class: "form-label"
                }, "Data Type:", -1 /* CACHED */)),
                _withDirectives(_createElementVNode("select", {
                  id: "valueBalanceType",
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((valueBalanceType).value = $event)),
                  class: "form-control",
                  "data-testid": "cost-subtotal-metric-config-value-balance-type"
                }, [...(_cache[15] || (_cache[15] = [
                  _createElementVNode("option", { value: "Costs" }, "Allocations", -1 /* CACHED */),
                  _createElementVNode("option", { value: "Benefits" }, "Savings", -1 /* CACHED */),
                  _createElementVNode("option", { value: "Net" }, "Balance (Allocations - Savings)", -1 /* CACHED */)
                ]))], 512 /* NEED_PATCH */), [
                  [_vModelSelect, valueBalanceType.value]
                ])
              ])
            ]),
            _createElementVNode("div", _hoisted_13, [
              _createElementVNode("div", _hoisted_14, [
                _cache[18] || (_cache[18] = _createElementVNode("label", { for: "denomination" }, "Currency denomination:", -1 /* CACHED */)),
                _withDirectives(_createElementVNode("select", {
                  id: "denomination",
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((denomination).value = $event)),
                  class: "form-control",
                  "data-testid": "cost-subtotal-metric-config-denomination"
                }, [...(_cache[17] || (_cache[17] = [
                  _createElementVNode("option", { value: "Unit" }, "Unit", -1 /* CACHED */),
                  _createElementVNode("option", { value: "Thousand" }, "Thousand", -1 /* CACHED */),
                  _createElementVNode("option", { value: "Million" }, "Million", -1 /* CACHED */)
                ]))], 512 /* NEED_PATCH */), [
                  [_vModelSelect, denomination.value]
                ])
              ])
            ]),
            _createElementVNode("fieldset", {
              class: _normalizeClass(["border p-3 mt-4", { 'border-danger': costIdsError.value }])
            }, [
              _createElementVNode("legend", {
                class: _normalizeClass(["w-auto px-2 text-primary", { 'text-danger': costIdsError.value }])
              }, " Opex Accounts ", 2 /* CLASS */),
              _cache[20] || (_cache[20] = _createElementVNode("div", { class: "mb-3 text-muted" }, " Choose which operating expense accounts should be included in this metric. ", -1 /* CACHED */)),
              (costIdsError.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_15, _toDisplayString(costIdsError.value), 1 /* TEXT */))
                : _createCommentVNode("v-if", true),
              _createCommentVNode(" Total toggle "),
              _createElementVNode("div", _hoisted_16, [
                _withDirectives(_createElementVNode("input", {
                  type: "checkbox",
                  class: "form-check-input",
                  id: "totalOpex",
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((totalOpex).value = $event))
                }, null, 512 /* NEED_PATCH */), [
                  [_vModelCheckbox, totalOpex.value]
                ]),
                _cache[19] || (_cache[19] = _createElementVNode("label", {
                  class: "form-check-label font-weight-medium",
                  for: "totalOpex"
                }, " Include all accounts ", -1 /* CACHED */))
              ]),
              _createCommentVNode(" Groups "),
              (!totalOpex.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groupedAccounts.value, (group) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: group.type,
                        class: "mb-4"
                      }, [
                        _createElementVNode("div", _hoisted_18, _toDisplayString(group.type), 1 /* TEXT */),
                        _createElementVNode("div", _hoisted_19, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.items, (acc) => {
                            return (_openBlock(), _createElementBlock("div", {
                              key: acc.Id,
                              class: "form-check mb-1"
                            }, [
                              _withDirectives(_createElementVNode("input", {
                                type: "checkbox",
                                class: "form-check-input",
                                id: acc.Id,
                                "onUpdate:modelValue": ($event: any) => ((acc.IsIncluded) = $event)
                              }, null, 8 /* PROPS */, _hoisted_20), [
                                [_vModelCheckbox, acc.IsIncluded]
                              ]),
                              _createElementVNode("label", {
                                class: "form-check-label",
                                for: acc.Id
                              }, _toDisplayString(acc.Name), 9 /* TEXT, PROPS */, _hoisted_21)
                            ]))
                          }), 128 /* KEYED_FRAGMENT */))
                        ])
                      ]))
                    }), 128 /* KEYED_FRAGMENT */))
                  ]))
                : _createCommentVNode("v-if", true)
            ], 2 /* CLASS */),
            _createVNode(MetricTimeWindowSettings, {
              startOffset: windowStartOffsetInMonths.value,
              "onUpdate:startOffset": _cache[5] || (_cache[5] = ($event: any) => ((windowStartOffsetInMonths).value = $event)),
              length: windowLengthInMonths.value,
              "onUpdate:length": _cache[6] || (_cache[6] = ($event: any) => ((windowLengthInMonths).value = $event))
            }, null, 8 /* PROPS */, ["startOffset", "length"]),
            _createVNode(MetricScheduleDependentSettings, {
              canDisable: false,
              isScheduleDependent: true,
              defaultProjectStartDate: defaultProjectStartDate.value,
              "onUpdate:defaultProjectStartDate": _cache[7] || (_cache[7] = ($event: any) => ((defaultProjectStartDate).value = $event)),
              startDateBoundary: startDateBoundary.value,
              "onUpdate:startDateBoundary": _cache[8] || (_cache[8] = ($event: any) => ((startDateBoundary).value = $event)),
              endDateBoundary: endDateBoundary.value,
              "onUpdate:endDateBoundary": _cache[9] || (_cache[9] = ($event: any) => ((endDateBoundary).value = $event)),
              applyBenefitsConfidenceFactor: applyBenefitsConfidenceFactor.value,
              "onUpdate:applyBenefitsConfidenceFactor": _cache[10] || (_cache[10] = ($event: any) => ((applyBenefitsConfidenceFactor).value = $event)),
              valueCommitmentType: valueCommitmentType.value,
              "onUpdate:valueCommitmentType": _cache[11] || (_cache[11] = ($event: any) => ((valueCommitmentType).value = $event))
            }, null, 8 /* PROPS */, ["defaultProjectStartDate", "startDateBoundary", "endDateBoundary", "applyBenefitsConfidenceFactor", "valueCommitmentType"])
          ], 32 /* NEED_HYDRATION */)
        ]))
      : _createCommentVNode("v-if", true)
  ]))
}
}

})